import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import {
  adminListCustomEarningApps,
  adminUpsertCustomEarningApp,
  adminDeleteCustomEarningApp,
} from "@/lib/earning-apps/admin.functions";
import { adminResubmitEarningAppsIndexNow } from "@/lib/earning-apps/admin.functions";
import { Loader2 } from "lucide-react";
import { EarningAppVideoPanel } from "@/components/earning-apps/EarningAppVideoPanel";

export const Route = createFileRoute("/admin/earning-apps")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Earning Apps Admin — DrHint" },
      { name: "description", content: "Manage DrHint earning-app reviews and their YouTube videos." },
      { property: "og:title", content: "Earning Apps Admin — DrHint" },
      { property: "og:description", content: "Manage earning-app reviews and review videos on DrHint." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: EarningAppsAdmin,
});

const SAMPLE_SPEC = `{
  "slug": "example-app",
  "name": "Example App",
  "category": "Cashback & rewards",
  "verdict": "Real",
  "founded": 2020,
  "hq": "Bengaluru, India",
  "regions": ["India", "Pakistan"],
  "minPayout": "Rs. 500 via UPI / EasyPaisa",
  "payoutMethods": ["UPI", "EasyPaisa", "Amazon Pay"],
  "earningsPerHour": "Rs. 40–100 depending on offers",
  "mechanism": "Example App gives cashback on partner-brand purchases and pays out weekly to UPI. Users install offer-wall apps from within the app for extra micro-rewards.",
  "realSignals": [
    "Registered Pvt Ltd entity in India with published GSTIN",
    "Verified UPI payouts logged publicly on Trustpilot since 2022",
    "Transparent points ledger inside the dashboard",
    "24-hr WhatsApp customer support with response SLA"
  ],
  "redFlags": [
    "Some offers reject after 30 days without clear reason",
    "Referral tier grinding can feel MLM-like for aggressive users"
  ],
  "bestFor": "Students and casual shoppers who buy through partner brands anyway",
  "worstFor": "Anyone expecting >Rs. 5,000/month without shopping activity",
  "ownerCompany": "Example Rewards Pvt Ltd",
  "yearsOperational": 6,
  "paymentProofNote": "Test wallet reached the Rs. 500 UPI threshold in 21 days on light shopping activity.",
  "competitorSlugs": ["swagbucks", "cashkaro"],
  "reviewerQuote": "Real, but income is directly tied to shopping habits — do not treat it as an income app.",
  "faqExtra": [
    { "q": "Is Example App safe for UPI payouts?", "a": "Yes — payouts settle via NPCI rails through a registered payment gateway. Never share your UPI PIN with anyone claiming to be support." },
    { "q": "How long does the first payout take?", "a": "Typically 7–14 days from first qualifying transaction, once the Rs. 500 threshold is crossed." }
  ],
  "reviews": [
    { "name": "Aditya Kumar", "city": "Pune", "country": "🇮🇳", "stars": 5, "date": "Feb 2026", "text": "Cashback hit UPI in 6 days. Legit app, using it 3 months now." },
    { "name": "Sana Malik", "city": "Karachi", "country": "🇵🇰", "stars": 4, "date": "Mar 2026", "text": "Payout thoda slow tha lekin mila. Support ne WhatsApp par help ki." }
  ],
  "hinglishSummary": "Example App ek cashback platform hai jo asli hai. Shopping karo, points milte hain, Rs. 500 pe UPI mein cash out kar sakte ho. Income app nahi — savings app samjho."
}`;

function EarningAppsAdmin() {
  const [state, setState] = useState<"loading" | "anon" | "not-admin" | "admin">("loading");
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return setState("anon");
      const { data } = await supabase.from("user_roles").select("role")
        .eq("user_id", user.id).eq("role", "admin").maybeSingle();
      setState(data ? "admin" : "not-admin");
    })().catch(() => setState("anon"));
  }, []);

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 py-8">
        <h1 className="text-2xl font-bold">💸 Earning Apps — Admin</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Paste a JSON spec to add or update an earning-app review page.
          Published rows auto-broadcast to IndexNow and enter the indexing queue.
        </p>
        {state === "loading" && <div className="mt-10 text-center"><Loader2 className="inline h-5 w-5 animate-spin" /></div>}
        {state === "anon" && <p className="mt-6 text-sm">Please sign in from <Link to="/admin-portal" className="text-primary underline">/admin-portal</Link> first.</p>}
        {state === "not-admin" && <p className="mt-6 text-sm">Your account is not an admin.</p>}
        {state === "admin" && <Dashboard />}
      </section>
    </Layout>
  );
}

type Row = { slug: string; name: string; verdict: string; is_published: boolean; updated_at: string };

function Dashboard() {
  const listFn = useServerFn(adminListCustomEarningApps);
  const upsertFn = useServerFn(adminUpsertCustomEarningApp);
  const deleteFn = useServerFn(adminDeleteCustomEarningApp);
  const resubmitFn = useServerFn(adminResubmitEarningAppsIndexNow);
  const [rows, setRows] = useState<Row[]>([]);
  const [json, setJson] = useState<string>(SAMPLE_SPEC);
  const [published, setPublished] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  async function reload() {
    try {
      const r = await listFn();
      setRows((r as { items: Row[] }).items);
    } catch (e) { setMsg((e as Error).message); }
  }
  useEffect(() => { reload(); }, []);

  async function save() {
    setBusy(true); setMsg("Validating & saving…");
    try {
      const spec = JSON.parse(json);
      const res = await upsertFn({ data: { spec, is_published: published } });
      const r = res as { url: string; indexnow: boolean };
      setMsg(`Saved ✓  Live at ${r.url} — IndexNow: ${r.indexnow ? "submitted ✓" : "not submitted"}`);
      await reload();
    } catch (e) {
      setMsg(`❌ ${(e as Error).message}`);
    } finally { setBusy(false); }
  }

  async function resubmitAll() {
    setBusy(true); setMsg("Submitting all published apps to IndexNow…");
    try {
      const res = await resubmitFn({}) as { ok: boolean; submitted: number };
      setMsg(`IndexNow: ${res.submitted} URLs submitted ${res.ok ? "✓" : "(endpoints rejected)"}`);
    } catch (e) { setMsg(`❌ ${(e as Error).message}`); }
    finally { setBusy(false); }
  }

  async function remove(slug: string) {
    if (!confirm(`Delete ${slug}?`)) return;
    setBusy(true);
    try {
      await deleteFn({ data: { slug } });
      setMsg(`Deleted ${slug}`);
      await reload();
    } catch (e) { setMsg((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <div className="mt-6 space-y-6">
      <EarningAppVideoPanel customApps={rows} />
      <section className="rounded-lg border-2 border-primary/30 bg-card p-4 space-y-3">
        <h2 className="text-lg font-bold">Add or update an earning app (JSON spec)</h2>
        <p className="text-xs text-muted-foreground">
          The spec follows the same <code>EarningApp</code> shape used by every static review page.
          Include 4–6 <code>reviews</code> (name, city, country flag emoji, stars 1–5, date, text) so
          the page shows real user testimonials. Add a <code>hinglishSummary</code> for the toggle.
        </p>
        <textarea
          value={json}
          onChange={(e) => setJson(e.target.value)}
          spellCheck={false}
          rows={22}
          className="w-full rounded-md border border-border bg-background p-3 font-mono text-xs"
        />
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} /> Published
          </label>
          <button disabled={busy} onClick={save} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
            {busy ? <Loader2 className="inline h-4 w-4 animate-spin" /> : "Save"}
          </button>
          <button type="button" onClick={() => setJson(SAMPLE_SPEC)} className="rounded-md border border-border px-3 py-2 text-xs">
            ↺ Reset to sample
          </button>
          <button type="button" disabled={busy} onClick={resubmitAll} className="rounded-md border border-primary/40 px-3 py-2 text-xs font-semibold">
            ⚡ Re-submit all to IndexNow
          </button>
          {msg && <span className="text-xs text-muted-foreground">{msg}</span>}
        </div>
      </section>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase">
            <tr>
              <th className="px-3 py-2 text-left">App</th>
              <th className="px-3 py-2 text-left">Verdict</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Updated</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-muted-foreground">No custom apps yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.slug}>
                <td className="px-3 py-2">
                  <Link to="/earning-apps-review/$slug" params={{ slug: r.slug }} className="text-primary hover:underline">
                    {r.name}
                  </Link>
                  <div className="text-[10px] text-muted-foreground">{r.slug}</div>
                </td>
                <td className="px-3 py-2 text-xs">{r.verdict}</td>
                <td className="px-3 py-2 text-xs">{r.is_published ? "Published" : "Draft"}</td>
                <td className="px-3 py-2 text-xs">{new Date(r.updated_at).toLocaleDateString()}</td>
                <td className="px-3 py-2">
                  <button onClick={() => remove(r.slug)} className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}